import {createForm, FormError, getValue, setResponse, SubmitHandler, valiForm} from "@modular-forms/solid";
import {A, useAction, useNavigate} from "@solidjs/router";
import * as v from "valibot";
import {
    ButtonGroup,
    FieldGroup,
    FormContainer,
    FormResponse,
    InputField,
    PasswordInputField
} from "~/components/form/ui/form";
import {signIn} from "~/sdk/auth";
import {Button} from "~/components/ui/Button";
import {Show} from "solid-js";

const SignInSchema = v.object({
    email: v.pipe(
        v.string(),
        v.nonEmpty('Please enter your email.'),
        v.email('The email address is badly formatted'),
    ),
    password: v.pipe(
        v.string(),
        v.nonEmpty('Please enter your password.'),
        v.minLength(8, 'Your password must be at least 8 characters long'),
    ),
})

type SignInForm = v.InferInput<typeof SignInSchema>

export default function SignInForm(props: {
    email?: string,
    redirect?: string,
    hideForgotPassword?: boolean,
    hideRegister?: boolean
}) {

    const [form, Form] = createForm<SignInForm>({
        validate: valiForm(SignInSchema),
        initialValues: {
            email: props.email,
        }
    })

    const navigate = useNavigate();
    const signInAction = useAction(signIn)

    const handleSubmit: SubmitHandler<SignInForm> = async (values, event) => {
        setResponse(form, {status: "info", message: "Signing in..."})
        try {
            await signInAction(values)
        } catch (e) {
            throw new FormError<SignInForm>(String(e))
        }
        setResponse(form, {status: "success", message: "Successfully signed in"});
        navigate(props.redirect ? props.redirect : "/account", {replace: true})
    }

    const email = () => getValue(form, "email")

    return (
        <FormContainer>
            <h1>Sign In</h1>
            <p>Enter your email and password to continue</p>
            <hr/>
            <Form.Form onSubmit={handleSubmit}>
                <FieldGroup>
                    <Form.Field name="email">
                        {(field, props) =>
                            <InputField {...props} type="email" label="Email" value={field.value} error={field.error}
                                        required/>
                        }
                    </Form.Field>
                    <Form.Field name="password">
                        {(field, props) =>
                            <PasswordInputField {...props} label="Password" value={field.value} error={field.error}
                                                required/>
                        }
                    </Form.Field>
                </FieldGroup>
                <FormResponse response={form.response}/>
                <ButtonGroup>
                    <Button type="submit" variant="primarySolid">Sign In</Button>
                </ButtonGroup>
                <Show when={!props.hideForgotPassword || !props.hideRegister}>
                    <ButtonGroup>
                        <Show when={!props.hideForgotPassword}>
                            <A href={`/auth/forgot-password${email() ? `?email=${email()}` : ""}`}><em>Forgot
                                Password?</em></A>
                        </Show>
                        <Show when={!props.hideRegister}>
                            <A href={`/auth/register${email() ? `?email=${email()}` : ""}`}>Register</A>
                        </Show>
                    </ButtonGroup>
                </Show>
            </Form.Form>
        </FormContainer>
    )
}
